
import { defineComponent, PropType, ref, computed } from "vue";
import OverlayModal from "./OverlayModal.vue";
import swal from "sweetalert2";
import useSlots from "@/modules/useSlots";
import { ProductListItem } from "@/models/Product";
import { CalendarSlot } from "@/models/Calendar";
import moment from "moment";
import { useI18n } from "vue-i18n";
import { Dictionary } from "lodash";

export default defineComponent({
  components: {
    OverlayModal,
  },
  name: "CalendarSlotModal",
  props: {
    products: {
      type: Array as PropType<ProductListItem[]>,
      default: () => [],
    },
    showProduct: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["save", "saveEvent", "close"],
  data() {
    return {};
  },
  async setup(props, { emit }: any) {
    const { t } = useI18n();
    const slotLimit = ref<string | null>(null);
    const endLimit = ref("date");
    const { saveCalendarEditLimitQty } = useSlots();
    const slot = ref<CalendarSlot>(new CalendarSlot());
    const isOpen = ref(false);
    const parentSlotDate = ref("");

    const getProductName = computed(
      () => props.products.find((x) => x.id == slot.value.productId)?.name ?? ""
    );

    function minusQuontity() {
      if (slot.value.limit == null) {
        return;
      }
      if (slot.value.limit <= 0) {
        return;
      }
      slot.value.limit--;
    }
    function plusQuontity() {
      if (slot.value.limit == null) {
        slot.value.limit = 1;
      } else {
        slot.value.limit++;
      }
    }

    function validate() {
      let limit = false;
      if (slotLimit.value) {
        const l = slot.value.limit ?? -1;
        if (String(l) !== "0") {
          limit =
            !((slot.value.limit || 0) * 1) ||
            l < 0 ||
            l > 1_000_000 ||
            l % 1 !== 0;
        }
      }
      return {
        limit: limit,
      };
    }
    const invalid = ref(validate());

    const back = () => {
      emit("close");
      isOpen.value = false;
    };
    const validateSaveSlot = async () => {
      invalid.value = validate();
      // has validation errors;
      if (Object.values(invalid.value).filter((x) => x).length > 0) {
        return;
      }
      const model = CalendarSlot.clone(slot.value);
      model.startDate = moment(model.startDate).format().substr(0, 10);
      // has Repeat
      if (model.repeatMode > 1) {
        if (endLimit.value == "count") {
          model.repeatEndDate = null;
          model.repeatCount = parseInt((model.repeatCount ?? 0).toString());
        } else {
          model.repeatCount = null;
          model.repeatEndDate = model.repeatEndDate
            ? moment(model.repeatEndDate).format().substr(0, 10)
            : null;
        }
      } else {
        model.repeatCount = null;
        model.repeatEndDate = null;
      }
      model.startTime;
      model.endTime;
      model.duration;
      if (!slotLimit.value) {
        model.limit = null;
      } else {
        model.limit = (model.limit || 0) * 1;
      }
      if (model.limit === 0) {
        const slotZeroQtyAccepted = await swal.fire({
          text: t("product.priceList.zeroLimit"),
          icon: "question",
          customClass: {
            confirmButton: "save-button-wrapper popup-bookit-button my-1 px-5",
            cancelButton: "close-button-wrapper popup-bookit-button my-1 px-5",
          },
          buttonsStyling: false,
          showCancelButton: true,
          confirmButtonText: t("button.yes"),
          cancelButtonText: t("button.no"),
        });
        if (!slotZeroQtyAccepted.isConfirmed) {
          return;
        }
      }
      swal.showLoading();
      const savedSlot = await saveCalendarEditLimitQty(model, parentSlotDate.value);
      if (!savedSlot) return;
      swal.close();
      emit("save", savedSlot);
      isOpen.value = false;
    };

    function save() {
      validateSaveSlot();
    }
    function open(newSlot: CalendarSlot, productIsEditable = true, changeDate = false) {
      Object.keys(invalid.value).forEach(
        (k) => ((invalid.value as Dictionary<boolean>)[k] = false)
      );
      slot.value = CalendarSlot.clone(newSlot);

      parentSlotDate.value = "";
      if (newSlot.originalSlotId) {
        parentSlotDate.value = moment(newSlot.startDate).format("YYYY-MM-DD");
      }
      slotLimit.value = slot.value.limit !== null ? "count" : null;
      isOpen.value = true;
    }
    function filterSlotLimitInput(event: any) {
      if (event.key.trim() === "" || isNaN(event.key)) {
        event.preventDefault();
      }
    }
    return {
      back,
      slot,
      isOpen,
      open,
      slotLimit,
      invalid,
      save,
      filterSlotLimitInput,
      minusQuontity,
      plusQuontity,
      getProductName,
    };
  },
});
